import React, { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';

interface HeroSlide {
  id: number;
  title?: string;
  subtitle?: string;
  image_path: string;
  image_alt?: string;
}

interface HeroSliderProps {
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

export const HeroSlider: React.FC<HeroSliderProps> = ({ 
  autoPlay = true, 
  autoPlayInterval = 5000 
}) => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // The required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50;

  useEffect(() => {
    loadSlides();
  }, []);

  useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, slides.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && slides.length > 1) {
      nextSlide();
    }
    if (isRightSwipe && slides.length > 1) {
      prevSlide();
    }
  };

  const loadSlides = async () => {
    try {
      setLoading(true);
      const response = await apiService.getHeroSlides();
      
      if (response.success && response.data && response.data.length > 0) {
        setSlides(response.data);
      } else {
        // Fallback to default image if no slides
        setSlides([{
          id: 0,
          title: 'Welcome to AARAN HOMES',
          
          image_path: '/maria-krasnova-qD7tpy_VozY-unsplash.jpg',
          image_alt: 'AARAN HOMES - Your Dream Home Awaits'
        }]);
      }
    } catch (err) {
      console.error('Error loading hero slides:', err);
      setError('Failed to load images');
      // Fallback to default image
      setSlides([{
        id: 0,
        title: 'Welcome to AARAN HOMES',
        
        image_path: '/Generated Image September 20, 2025 - 8_36PM.png',
        image_alt: 'AARAN HOMES - Your Dream Home Awaits'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  if (loading) {
    return (
      <div className="relative h-[60vh] md:h-[70vh] lg:h-[80vh] bg-gray-200 animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="relative h-[60vh] md:h-[70vh] lg:h-[80vh] bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-4xl font-bold mb-4">Welcome to AARAN HOMES</h1>
          
        </div>
      </div>
    );
  }

  const currentSlideData = slides[currentSlide];
  const imageUrl = currentSlideData.image_path.startsWith('/') 
    ? currentSlideData.image_path 
    : apiService.getHeroSlideUrl(currentSlideData.image_path);

  return (
    <div 
      className="relative h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden"
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Slides Container */}
      <div className="relative h-full">
        {slides.map((slide, index) => {
          const slideImageUrl = slide.image_path.startsWith('/') 
            ? slide.image_path 
            : apiService.getHeroSlideUrl(slide.image_path);
          
          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slideImageUrl}
                alt={slide.image_alt || slide.title || 'Hero slide'}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
              
              {/* Light overlay for better text readability */}
              <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            </div>
          );
        })}
      </div>

      {/* Fixed Content at Bottom */}
      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 leading-tight">
            {currentSlideData.title || 'Welcome to AARAN HOMES'}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-4 sm:mb-6 leading-relaxed">
            {currentSlideData.subtitle || 'Find Your Dream Property Today'}
          </p>
          <div className="flex flex-row gap-2 sm:gap-4 justify-center items-center">
            <a
              href="/properties"
              className="flex-1 max-w-[140px] sm:max-w-none sm:w-auto bg-red-600 hover:bg-red-700 text-white px-3 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold transition-colors duration-200 text-center"
            >
              View Properties
            </a>
            <a
              href="/contact"
              className="flex-1 max-w-[140px] sm:max-w-none sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 px-3 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold transition-colors duration-200 text-center"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Dots Indicator */}
      {slides.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors duration-200 ${
                index === currentSlide ? 'bg-white' : 'bg-white bg-opacity-50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};